import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Class as ClassIcon
} from '@mui/icons-material';
import './ClassFeed.css';
import ClassPostCard from './ClassPostCard';

interface ClassInfo {
  id: number;
  name: string;
  subject: string | null;
  grade_level: string | null;
  description: string | null;
  teacher?: {
    id: number;
    name: string;
    email: string;
  };
}

interface ClassPost {
  id: number;
  class_id: number;
  class_name: string;
  author_id: number;
  author_name: string;
  post_type: string;
  title: string | null;
  content: string;
  is_pinned: boolean;
  created_at: string;
  updated_at: string;
  comments_count: number;
  attachments: any[];
  attachments_count: number;
}

const StudentClassFeed: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t } = useTranslation();

  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [posts, setPosts] = useState<ClassPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token && classId) {
      fetchClassInfo();
      fetchPosts();
    }
  }, [token, classId]);

  const fetchClassInfo = async () => {
    try {
      const response = await fetch(`/api/classes/${classId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch class info');

      const data = await response.json();
      if (data.success) {
        setClassInfo(data.class);
      }
    } catch (err: any) {
      console.error('Error fetching class info:', err);
      setError(err.message || 'Failed to load class information');
    }
  };

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/classes/${classId}/posts`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('Fetch posts response status:', response.status);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Fetch posts error:', errorData);
        throw new Error(errorData.error || `Failed to fetch posts (${response.status})`);
      }

      const data = await response.json();
      console.log('Fetch posts data:', data);
      if (data.success) {
        setPosts(data.posts);
      }
    } catch (err: any) {
      console.error('Error fetching posts:', err);
      setError(err.message || 'Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/student/dashboard');
  };

  const pinnedPosts = posts.filter(post => post.is_pinned);
  const recentPosts = posts.filter(post => !post.is_pinned);

  return (
          <Box sx={{ 
            minHeight: '100vh', 
            bgcolor: '#f5f7fa', 
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            {/* Class Header Banner */}
            {classInfo && (
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  py: 4,
                  px: 4,
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  width: '100%'
                }}
              >
                <Box sx={{ maxWidth: '90%', margin: '0 auto', width: '100%' }}>
                  <Button
                    startIcon={<BackIcon />}
                    onClick={handleBack}
                    sx={{ 
                      color: 'white',
                      textTransform: 'none',
                      mb: 2,
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    {t('common.back')}
                  </Button>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <ClassIcon sx={{ fontSize: 56 }} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: { xs: '2rem', md: '3rem' } }}>
                        {classInfo.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                        {classInfo.subject && (
                          <Chip
                            label={classInfo.subject}
                            size="medium"
                            sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 600 }}
                          />
                        )}
                        {classInfo.grade_level && (
                          <Chip
                            label={classInfo.grade_level}
                            size="medium"
                            sx={{ bgcolor: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 600 }}
                          />
                        )}
                      </Box>
                      {classInfo.teacher && (
                        <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 500 }}>
                          👨‍🏫 {classInfo.teacher.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {classInfo.description && (
                    <Typography variant="body1" sx={{ mt: 2, opacity: 0.95, fontSize: '1.1rem' }}>
                      {classInfo.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}

            {/* Feed Content */}
            <Box sx={{ 
              maxWidth: '90%', 
              margin: '0 auto', 
              py: 4, 
              px: 3,
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>

              {/* Error Alert */}
              {error && (
                <Alert
                  severity="error"
                  onClose={() => setError(null)}
                  sx={{ mb: 3, width: '100%', maxWidth: 800 }}
                >
                  {error}
                </Alert>
              )}

              {/* Loading State */}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, width: '100%' }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Posts Feed */}
              {!loading && (
                <Box sx={{ width: '100%', maxWidth: 800 }}>
                  {/* Pinned Posts */}
                  {pinnedPosts.length > 0 && (
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="h5" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, fontWeight: 600 }}>
                        📌 {t('classFeed.pinnedPosts')}
                      </Typography>
                      {pinnedPosts.map((post) => (
                        <ClassPostCard
                          key={post.id}
                          post={post}
                        />
                      ))}
                    </Box>
                  )}

                  {/* Recent Posts */}
                  {recentPosts.length > 0 ? (
                    <Box>
                      {pinnedPosts.length > 0 && (
                        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                          {t('classFeed.recentPosts')}
                        </Typography>
                      )}
                      {recentPosts.map((post) => (
                        <ClassPostCard
                          key={post.id}
                          post={post}
                        />
                      ))}
                    </Box>
                  ) : (
                    !pinnedPosts.length && (
                      <Box
                        sx={{
                          textAlign: 'center',
                          py: 8,
                          color: 'text.secondary',
                          bgcolor: 'white',
                          borderRadius: 2,
                          boxShadow: 1
                        }}
                      >
                        <Typography variant="h6" gutterBottom>
                          💬 {t('classFeed.noPosts')}
                        </Typography>
                        <Typography variant="body2">
                          {t('classFeed.noPostsDescription')}
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              )}
            </Box>
          </Box>
  );
};

export default StudentClassFeed;

